import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import CustomModal from "@/components/ui/CustomModal.web";
import VehiclePlate from "@/components/ui/VehiclePlate";
import VehiclePlateInput from "@/components/ui/VehiclePlateInput";
import { resolveHomeRoute } from "@/constants/AppNavigation";
import { useAuth } from "@/context/AuthContext";
import { firestore } from "@/firebase/web";
import type { VehiclePlate as VehiclePlateType } from "@/types/vehicle-plate";
import { formatVehiclePlateToStr } from "@/utils/formatVehiclePlate";
import {
  hasCompletedInitialOnboardingTour,
  hasFollowedPlate,
  isPhoneOnlyAuthUser,
  isPhoneOnlyUser,
} from "@/utils/onboarding.web";
import { isUsernameAvailable } from "@/utils/username.web";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import "../(tabs)/profile.css";

type StepId = "fullName" | "username" | "followedPlate";

type OnboardingStep = { id: StepId; titleKey: string; optional: boolean };

const BASE_STEP_ORDER: OnboardingStep[] = [
  { id: "fullName", titleKey: "ONBOARDING_STEP_FULL_NAME", optional: false },
  { id: "username", titleKey: "USERNAME", optional: false },
];

const PHONE_ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: "followedPlate",
    titleKey: "ONBOARDING_PHONE_PLATE_TITLE",
    optional: false,
  },
];

const DEFAULT_PHONE_ONBOARDING_PLATE_COUNTRY = "PY";

const deriveInitialStepIndex = (userData: any, stepOrder: OnboardingStep[]) => {
  const persistedStepId = userData?.onboardingPrefs?.initialOnboardingStepId;
  const persistedStepIndex = stepOrder.findIndex(
    (step) => step.id === persistedStepId,
  );

  if (persistedStepIndex >= 0) {
    return persistedStepIndex;
  }

  if (stepOrder === PHONE_ONBOARDING_STEPS) {
    return 0;
  }

  if (!userData?.firstName || !userData?.lastName || !userData?.ageConfirmed)
    return 0;
  if (!userData?.username) return 1;

  return stepOrder.length - 1;
};

export default function WebOnboardingScreen() {
  const router = useRouter();
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const [stepOrder, setStepOrder] = useState<OnboardingStep[]>(BASE_STEP_ORDER);
  const [isPhoneFlow, setIsPhoneFlow] = useState(false);
  const [plates, setPlates] = useState<VehiclePlateType[]>([]);
  const vehiclePlateInputRef = useRef<{
    submit: () => void;
    getPendingPlateCandidate: () => VehiclePlateType | null;
  } | null>(null);
  const [pendingPlateConfirm, setPendingPlateConfirm] =
    useState<VehiclePlateType | null>(null);

  const [initializing, setInitializing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameAvailabilityError, setUsernameAvailabilityError] =
    useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
  } | null>(null);

  const [originalUsername, setOriginalUsername] = useState("");

  const persistOnboardingProgress = useCallback(
    async (
      nextStepIndex: number,
      completed: boolean,
      options?: { clearStepId?: boolean },
    ) => {
      if (!user) return;

      const clampedIndex = Math.max(
        0,
        Math.min(nextStepIndex, stepOrder.length - 1),
      );
      const nextStepId = stepOrder[clampedIndex]?.id;

      const payload: Record<string, any> = {
        "onboardingPrefs.initialOnboardingCompleted": completed,
      };

      if (!completed && nextStepId) {
        payload["onboardingPrefs.initialOnboardingStepId"] = nextStepId;
      } else if (options?.clearStepId) {
        payload["onboardingPrefs.initialOnboardingStepId"] = null;
      }

      await setDoc(doc(firestore, "users", user.uid), payload, { merge: true });
    },
    [user, stepOrder],
  );

  const activeStep =
    stepOrder[currentStep] || stepOrder[Math.max(stepOrder.length - 1, 0)];
  const isLastStep = currentStep >= stepOrder.length - 1;

  const canSkipCurrentStep = useMemo(() => activeStep.optional, [activeStep]);

  const validateUsername = useCallback(
    (text: string) => {
      if (text.trim() === "") {
        setUsernameError(t("USERNAME_REQUIRED"));
      } else if (/\s/.test(text)) {
        setUsernameError(t("USERNAME_CANNOT_CONTAIN_SPACES"));
      } else if (text.length > 0 && text.length < 3) {
        setUsernameError(t("USERNAME_TOO_SHORT"));
      } else if (text.length > 0 && !/^[a-zA-Z0-9_.]+$/.test(text)) {
        setUsernameError(t("USERNAME_INVALID_CHARACTERS"));
      } else if (text.length > 0 && !/^[a-zA-Z0-9]/.test(text)) {
        setUsernameError(t("USERNAME_INVALID_START"));
      } else {
        setUsernameError("");
      }
      setUsername(text);
    },
    [t],
  );

  const checkUsernameAvailability = useCallback(
    async (candidateUsername: string) => {
      if (!user) return false;

      return isUsernameAvailable(firestore, candidateUsername, user.uid);
    },
    [user],
  );

  useEffect(() => {
    if (!isLoading && !user) {
      router.replace("/(auth)/login");
    }
  }, [isLoading, user, router]);

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      if (!user) {
        if (mounted) setInitializing(false);
        return;
      }

      try {
        const userDocRef = doc(firestore, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        const userData = userDoc.exists() ? userDoc.data() : null;

        if (!mounted) return;

        const phoneFlow =
          isPhoneOnlyUser(userData) || isPhoneOnlyAuthUser(user);
        const activeStepOrder = phoneFlow
          ? PHONE_ONBOARDING_STEPS
          : BASE_STEP_ORDER;
        setIsPhoneFlow(phoneFlow);
        setStepOrder(activeStepOrder);

        const hasCompletedTour = hasCompletedInitialOnboardingTour(userData);
        if (phoneFlow) {
          if (hasCompletedTour && hasFollowedPlate(userData)) {
            router.replace(resolveHomeRoute(userData?.defaultHomeTab));
            return;
          }
        } else {
          const fallbackNameParts = (userData?.displayName || "")
            .trim()
            .split(/\s+/)
            .filter(Boolean);
          const resolvedFirstName =
            userData?.firstName || fallbackNameParts[0] || "";
          const resolvedLastName =
            userData?.lastName || fallbackNameParts.slice(1).join(" ") || "";

          setFirstName(resolvedFirstName);
          setLastName(resolvedLastName);
          setUsername(userData?.username || "");
          setOriginalUsername(userData?.username || "");
          setAgeConfirmed(!!userData?.ageConfirmed);

          if (hasCompletedTour) {
            router.replace(resolveHomeRoute(userData?.defaultHomeTab));
            return;
          }
        }

        const initialStepIndex = deriveInitialStepIndex(
          userData,
          activeStepOrder,
        );
        setCurrentStep(initialStepIndex);

        const explicitCompletion =
          userData?.onboardingPrefs?.initialOnboardingCompleted;
        if (explicitCompletion !== false) {
          await setDoc(
            userDocRef,
            {
              "onboardingPrefs.initialOnboardingCompleted": false,
              "onboardingPrefs.initialOnboardingStepId":
                activeStepOrder[initialStepIndex]?.id,
            },
            { merge: true },
          );
        }
      } catch (error) {
        console.error("Error loading onboarding data:", error);
      } finally {
        if (mounted) setInitializing(false);
      }
    };

    bootstrap();

    return () => {
      mounted = false;
    };
  }, [user, router, stepOrder]);

  useEffect(() => {
    if (!user) return;

    const normalizedUsername = username.trim().toLowerCase();
    const currentUsername = (originalUsername || "").trim().toLowerCase();

    if (
      !normalizedUsername ||
      !!usernameError ||
      normalizedUsername === currentUsername
    ) {
      setUsernameAvailabilityError("");
      setIsCheckingUsername(false);
      return;
    }

    let cancelled = false;
    setIsCheckingUsername(true);

    const timeoutId = setTimeout(async () => {
      try {
        const available = await checkUsernameAvailability(normalizedUsername);
        if (cancelled) return;

        setUsernameAvailabilityError(
          available ? "" : t("USERNAME_ALREADY_TAKEN"),
        );
      } catch (error) {
        console.error("Error checking username availability:", error);
        if (!cancelled) {
          setUsernameAvailabilityError("");
        }
      } finally {
        if (!cancelled) {
          setIsCheckingUsername(false);
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    user,
    username,
    usernameError,
    originalUsername,
    checkUsernameAvailability,
    t,
  ]);

  const goToNextStep = async () => {
    if (isLastStep) {
      const payload: Record<string, any> = {
        "onboardingPrefs.initialOnboardingCompleted": true,
        "onboardingPrefs.initialOnboardingStepId": null,
      };
      if (isPhoneFlow) {
        payload["onboardingPrefs.plateTourCompleted"] = true;
      }
      if (user) {
        await setDoc(doc(firestore, "users", user.uid), payload, {
          merge: true,
        });
      }
      // Re-read the user doc so the redirect honors a defaultHomeTab the
      // user may have set earlier (not expected during first onboarding,
      // but keeps this path consistent with the other two completion
      // branches).
      let defaultHomeTab: "feed" | "groups" | undefined;
      if (user) {
        try {
          const latest = await getDoc(doc(firestore, "users", user.uid));
          const data = latest.exists() ? latest.data() : null;
          defaultHomeTab = data?.defaultHomeTab;
        } catch (err) {
          console.warn("Unable to read defaultHomeTab for redirect:", err);
        }
      }
      router.replace(resolveHomeRoute(defaultHomeTab));
      return;
    }

    const nextStepIndex = Math.min(currentStep + 1, stepOrder.length - 1);
    await persistOnboardingProgress(nextStepIndex, false);
    setCurrentStep(nextStepIndex);
  };

  const showError = (message: string) => {
    setModalInfo({
      title: t("ONBOARDING_VALIDATION_ERROR_TITLE"),
      message,
    });
  };

  const saveFullNameStep = async () => {
    if (!user) return;

    const normalizedFirstName = firstName.trim();
    const normalizedLastName = lastName.trim();

    if (!normalizedFirstName) {
      showError(t("PLEASE_ENTER_FIRST_NAME"));
      return;
    }

    if (!normalizedLastName) {
      showError(t("PLEASE_ENTER_LAST_NAME"));
      return;
    }

    if (!ageConfirmed) {
      showError(t("ONBOARDING_ERROR_AGE_CONFIRMATION_REQUIRED"));
      return;
    }

    const normalizedFullName = `${normalizedFirstName} ${normalizedLastName}`;
    const userDocRef = doc(firestore, "users", user.uid);

    await setDoc(
      userDocRef,
      {
        displayName: normalizedFullName,
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        fullNameSet: true,
        ageConfirmed: true,
      },
      { merge: true },
    );

    await goToNextStep();
  };

  const saveUsernameStep = async () => {
    if (!user) return;

    const normalizedUsername = username.trim().toLowerCase();

    if (usernameError) {
      showError(usernameError);
      return;
    }

    if (usernameAvailabilityError) {
      showError(usernameAvailabilityError);
      return;
    }

    if (!normalizedUsername) {
      showError(t("ONBOARDING_ERROR_USERNAME_REQUIRED"));
      return;
    }

    if (normalizedUsername.length < 3) {
      showError(t("ONBOARDING_ERROR_USERNAME_TOO_SHORT"));
      return;
    }

    if (!/^[a-z0-9_.]+$/.test(normalizedUsername)) {
      showError(t("ONBOARDING_ERROR_USERNAME_INVALID"));
      return;
    }

    if (normalizedUsername !== originalUsername) {
      const available = await isUsernameAvailable(
        firestore,
        normalizedUsername,
        user.uid,
      );
      if (!available) {
        showError(t("ONBOARDING_ERROR_USERNAME_TAKEN"));
        return;
      }
    }

    const userDocRef = doc(firestore, "users", user.uid);
    await setDoc(userDocRef, { username: normalizedUsername }, { merge: true });
    setOriginalUsername(normalizedUsername);
    setUsername(normalizedUsername);
    setUsernameAvailabilityError("");

    await goToNextStep();
  };

  const writeFollowedPlatesAndAdvance = async (
    platesToWrite: VehiclePlateType[],
  ) => {
    if (!user) return;
    const formattedPlates = platesToWrite.map(formatVehiclePlateToStr);
    await setDoc(
      doc(firestore, "users", user.uid),
      { followedPlates: formattedPlates },
      { merge: true },
    );
    await goToNextStep();
  };

  const saveFollowedPlateStep = async () => {
    if (!user) return;

    // If the user typed a plate but didn't press Enter, treat it as a
    // pending candidate and ask them to confirm before we commit it.
    if (plates.length === 0) {
      const pending =
        vehiclePlateInputRef.current?.getPendingPlateCandidate?.() || null;
      if (pending && pending.plate) {
        setPendingPlateConfirm(pending);
        return;
      }
      showError(t("ONBOARDING_ERROR_PLATE_REQUIRED"));
      return;
    }

    await writeFollowedPlatesAndAdvance(plates);
  };

  const confirmPendingPlate = async () => {
    if (!user || !pendingPlateConfirm) return;
    const candidate = pendingPlateConfirm;
    setPendingPlateConfirm(null);
    setPlates([candidate]);
    try {
      setSaving(true);
      await writeFollowedPlatesAndAdvance([candidate]);
    } catch (error) {
      console.error("Error saving confirmed plate:", error);
      showError(t("ONBOARDING_ERROR_STEP_SAVE"));
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    try {
      setSaving(true);

      switch (activeStep.id) {
        case "fullName":
          await saveFullNameStep();
          break;
        case "username":
          await saveUsernameStep();
          break;
        case "followedPlate":
          await saveFollowedPlateStep();
          break;
      }
    } catch (error) {
      console.error("Error saving onboarding step:", error);
      showError(t("ONBOARDING_ERROR_STEP_SAVE"));
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    await goToNextStep();
  };

  const handleBack = async () => {
    const prevStepIndex = Math.max(0, currentStep - 1);
    await persistOnboardingProgress(prevStepIndex, false);
    setCurrentStep(prevStepIndex);
  };

  const disableContinue =
    saving ||
    (activeStep.id === "fullName" && !ageConfirmed) ||
    (activeStep.id === "username" &&
      (isCheckingUsername || !!usernameError || !!usernameAvailabilityError));

  if (isLoading || initializing) {
    return (
      <SafeAreaView
        style={styles.safeAreaViewContainer}
        edges={["top", "bottom"]}
      >
        <Head>
          <title>{t("PAGE_TITLE_ONBOARDING")}</title>
        </Head>
        <ThemedView style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#4285F4" />
        </ThemedView>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={styles.safeAreaViewContainer}
      edges={["top", "bottom"]}
    >
      <Head>
        <title>{t("PAGE_TITLE_ONBOARDING")}</title>
      </Head>
      <ThemedView style={styles.pageContainer}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <ThemedView style={styles.card}>
            <ThemedView style={styles.progressLinesContainer}>
              {stepOrder.map((step, index) => (
                <View
                  key={step.id}
                  style={[
                    styles.progressLine,
                    index <= currentStep && styles.progressLineActive,
                  ]}
                />
              ))}
            </ThemedView>

            {/* <ThemedText style={styles.subtitle}>
              {t(activeStep.titleKey)}
            </ThemedText> */}

            {activeStep.id === "fullName" && (
              <>
                <ThemedText style={styles.label}>
                  {t("FIRST_NAME")} *
                </ThemedText>
                <TextInput
                  style={styles.input}
                  value={firstName}
                  onChangeText={setFirstName}
                  placeholder={t("FIRST_NAME")}
                  placeholderTextColor="#888"
                />
                <ThemedText style={styles.label}>{t("LAST_NAME")} *</ThemedText>
                <TextInput
                  style={styles.input}
                  value={lastName}
                  onChangeText={setLastName}
                  placeholder={t("LAST_NAME")}
                  placeholderTextColor="#888"
                />
                <label className="age-confirm-row">
                  <input
                    type="checkbox"
                    className="age-confirm-input"
                    checked={ageConfirmed}
                    onChange={(event) =>
                      setAgeConfirmed(event.target.checked)
                    }
                  />
                  <span
                    aria-hidden="true"
                    className={
                      ageConfirmed
                        ? "age-confirm-box age-confirm-box--checked"
                        : "age-confirm-box"
                    }
                  >
                    {ageConfirmed && (
                      <span className="age-confirm-check">✓</span>
                    )}
                  </span>
                  <ThemedText style={styles.ageConfirmLabel}>
                    {t("ONBOARDING_AGE_CONFIRMATION_LABEL")}
                  </ThemedText>
                </label>
              </>
            )}

            {activeStep.id === "username" && (
              <>
                <ThemedText style={styles.label}>{t("USERNAME")} *</ThemedText>
                <TextInput
                  style={styles.input}
                  value={username}
                  onChangeText={validateUsername}
                  autoCapitalize="none"
                  maxLength={20}
                  placeholder={t("ONBOARDING_USERNAME_PLACEHOLDER")}
                  placeholderTextColor="#888"
                />
                {usernameError ? (
                  <ThemedText style={styles.errorText}>
                    {usernameError}
                  </ThemedText>
                ) : usernameAvailabilityError ? (
                  <ThemedText style={styles.errorText}>
                    {usernameAvailabilityError}
                  </ThemedText>
                ) : isCheckingUsername ? (
                  <ThemedText style={styles.usernameStatusText}>
                    {t("CHECKING_USERNAME_AVAILABILITY")}
                  </ThemedText>
                ) : null}
              </>
            )}

            {activeStep.id === "followedPlate" && (
              <>
                <ThemedText style={styles.title}>
                  {t("ONBOARDING_PHONE_PLATE_TITLE")}
                </ThemedText>
                <ThemedText style={styles.hint}>
                  {t("ONBOARDING_PHONE_PLATE_SUBTITLE")}
                </ThemedText>
                <VehiclePlateInput
                  ref={vehiclePlateInputRef as any}
                  plates={plates}
                  onChange={setPlates}
                  limit={1}
                  defaultCountry={DEFAULT_PHONE_ONBOARDING_PLATE_COUNTRY}
                />
              </>
            )}

            <ThemedView style={styles.footerButtons}>
              <ThemedView style={styles.footerLeftGroup}>
                {currentStep > 0 && (
                  <TouchableOpacity
                    onPress={handleBack}
                    disabled={saving}
                    style={styles.backButton}
                  >
                    <ThemedText type="link">{t("ONBOARDING_BACK")}</ThemedText>
                  </TouchableOpacity>
                )}

                {canSkipCurrentStep && (
                  <TouchableOpacity
                    onPress={handleSkip}
                    disabled={saving}
                    style={styles.skipButton}
                  >
                    <ThemedText type="link">{t("ONBOARDING_SKIP")}</ThemedText>
                  </TouchableOpacity>
                )}
              </ThemedView>

              {activeStep.id === "followedPlate" ? (
                <Button
                  onPress={handleNext}
                  disabled={disableContinue}
                  text={t("CONFIRM")}
                  style={styles.followedPlateCta}
                />
              ) : (
                <TouchableOpacity
                  onPress={handleNext}
                  disabled={disableContinue}
                  style={styles.continueButton}
                >
                  <ThemedText
                    type="link"
                    style={
                      disableContinue ? styles.disabledLinkText : undefined
                    }
                  >
                    {isLastStep
                      ? isPhoneFlow
                        ? t("CONFIRM")
                        : t("ONBOARDING_FINISH")
                      : t("ONBOARDING_CONTINUE")}
                  </ThemedText>
                </TouchableOpacity>
              )}
            </ThemedView>
          </ThemedView>
        </ScrollView>

        <CustomModal
          visible={!!modalInfo}
          onClose={() => setModalInfo(null)}
          title={modalInfo?.title || ""}
        >
          <ThemedText>{modalInfo?.message}</ThemedText>
          <Button
            text="OK"
            onPress={() => setModalInfo(null)}
            style={{ marginTop: 20 }}
          />
        </CustomModal>

        <CustomModal
          visible={!!pendingPlateConfirm}
          onClose={() => setPendingPlateConfirm(null)}
          title={t("ONBOARDING_PLATE_PENDING_CONFIRM_TITLE")}
        >
          <ThemedText style={styles.pendingPlateConfirmText}>
            {t("ONBOARDING_PLATE_PENDING_CONFIRM_MESSAGE", {
              plate: pendingPlateConfirm?.plate ?? "",
            })}
          </ThemedText>
          {pendingPlateConfirm && (
            <View style={styles.pendingPlatePreview}>
              <VehiclePlate
                plate={pendingPlateConfirm.plate}
                country={pendingPlateConfirm.country}
              />
            </View>
          )}
          <View style={styles.pendingPlateConfirmButtons}>
            <Button
              text={t("CANCEL")}
              onPress={() => setPendingPlateConfirm(null)}
              style={[
                styles.pendingPlateConfirmButton,
                styles.pendingPlateConfirmCancel,
              ]}
              textStyle={styles.pendingPlateConfirmCancelText}
            />
            <Button
              text={t("CONFIRM")}
              onPress={confirmPendingPlate}
              style={styles.pendingPlateConfirmButton}
              disabled={saving}
            />
          </View>
        </CustomModal>
      </ThemedView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeAreaViewContainer: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  pageContainer: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  card: {
    width: "100%",
    maxWidth: 620,
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 24,
    gap: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: "600",
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 10,
  },
  progressLinesContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 6,
    marginBottom: 8,
  },
  progressLine: {
    flex: 1,
    height: 4,
    borderRadius: 999,
    backgroundColor: "#d9d9d9",
  },
  progressLineActive: {
    backgroundColor: "#0a7ea4",
  },
  label: {
    fontSize: 16,
    marginTop: 6,
  },
  hint: {
    fontSize: 13,
    opacity: 0.75,
    marginTop: 4,
  },
  input: {
    width: "100%",
    minHeight: 48,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    paddingHorizontal: 12,
    backgroundColor: "#fafafa",
    color: "#000",
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: "top",
    paddingTop: 10,
  },
  webInputContainer: {
    width: "100%",
  },
  ageConfirmLabel: {
    fontSize: 14,
    flex: 1,
  },
  webInput: {
    width: "100%",
    minHeight: 48,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#ccc",
    padding: 10,
    fontSize: 16,
    backgroundColor: "#fafafa",
  },
  errorText: {
    color: "red",
    fontSize: 12,
  },
  usernameStatusText: {
    fontSize: 12,
    opacity: 0.75,
  },
  footerButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 14,
  },
  footerLeftGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  backButton: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  skipButton: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  continueButton: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  followedPlateCta: {
    flex: 1,
    maxWidth: "100%",
  },
  disabledLinkText: {
    opacity: 0.5,
  },
  pendingPlateConfirmText: {
    marginBottom: 12,
    textAlign: "center",
  },
  pendingPlatePreview: {
    alignItems: "center",
    marginBottom: 16,
  },
  pendingPlateConfirmButtons: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 12,
    marginTop: 8,
    width: "100%",
  },
  pendingPlateConfirmButton: {
    flex: 1,
    width: "auto",
  },
  pendingPlateConfirmCancel: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#0a7ea4",
  },
  pendingPlateConfirmCancelText: {
    color: "#0a7ea4",
  },
});
